<template>
    <AuthLayout v-bind="appSharedData.getAuthLayoutProps()">
        <template #title> Create a new account </template>

        <template #card-content>
            <div v-if="hasSocialLoginOptions">
                <div class="space-y-4">
                    <GoogleSocialAuthButton
                        v-if="googleSocialLoginEnabled"
                        :url="urlFactory.makeGoogleOAuthRegisterRedirectUrl()"
                    >
                        Sign up with Google
                    </GoogleSocialAuthButton>

                    <MicrosoftSocialAuthButton
                        v-if="microsoftSocialLoginEnabled"
                        :url="
                            urlFactory.makeMicrosoftOAuthRegisterRedirectUrl()
                        "
                    >
                        Sign up with Microsoft
                    </MicrosoftSocialAuthButton>
                </div>
                <div class="relative my-4">
                    <div class="absolute inset-0 flex items-center">
                        <div class="w-full border-t border-gray-200"></div>
                    </div>
                    <div
                        class="relative flex justify-center text-sm font-medium leading-6"
                    >
                        <span class="bg-white px-6 text-gray-900">Or</span>
                    </div>
                </div>
            </div>

            <ApiForm
                :url="urlFactory.makeAuthRegisterPageUrl()"
                method="POST"
                ref="apiFormRef"
            >
                <div class="flex flex-col md:flex-row gap-2">
                    <FormControl
                        label="First name"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Register_FirstName,
                            )
                        "
                    >
                        <TextInput
                            type="text"
                            class="w-full"
                            :name="
                                RequestBodyKeyEnum.Auth_User_Register_FirstName
                            "
                            required
                        />
                    </FormControl>
                    <FormControl
                        label="Last name"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Register_LastName,
                            )
                        "
                    >
                        <TextInput
                            type="text"
                            class="w-full"
                            :name="
                                RequestBodyKeyEnum.Auth_User_Register_LastName
                            "
                        />
                    </FormControl>
                </div>

                <div>
                    <FormControl
                        label="Email"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Register_Email,
                            )
                        "
                    >
                        <TextInput
                            type="email"
                            class="w-full"
                            :name="RequestBodyKeyEnum.Auth_User_Register_Email"
                            required
                        />
                    </FormControl>
                </div>

                <div class="flex flex-col md:flex-row gap-2">
                    <FormControl
                        label="Password"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Register_Password,
                            )
                        "
                    >
                        <TextInput
                            type="password"
                            class="w-full"
                            :name="
                                RequestBodyKeyEnum.Auth_User_Register_Password
                            "
                            required
                        />
                    </FormControl>
                    <FormControl
                        label="Password confirmation"
                        :error="
                            apiFormRef?.response?.getValidationError(
                                RequestBodyKeyEnum.Auth_User_Register_PasswordConfirmation,
                            )
                        "
                    >
                        <TextInput
                            type="password"
                            class="w-full"
                            :name="
                                RequestBodyKeyEnum.Auth_User_Register_PasswordConfirmation
                            "
                            required
                        />
                    </FormControl>
                </div>

                <div>
                    <Button
                        @click="apiFormRef?.submit()"
                        class="w-full mt-4"
                        :loading="apiFormRef?.loading.valueOf()"
                    >
                        Register
                    </Button>
                </div>

                <div class="flex flex-col mt-4 space-y-1">
                    <div class="text-sm leading-6">
                        <a
                            :href="urlFactory.makeRelativeAuthLoginPageUrl('')"
                            class="font-semibold text-primary hover:text-primary-shade"
                        >
                            Already have an account?
                        </a>
                    </div>
                    <div class="text-sm leading-6">
                        <a
                            :href="urlFactory.makeAuthForgotPasswordPageUrl()"
                            class="font-semibold text-primary hover:text-primary-shade"
                        >
                            Forgot your password?
                        </a>
                    </div>
                </div>

                <div
                    v-if="turnstileEnabled"
                    class="mt-4 flex justify-center"
                >
                    <div
                        class="cf-turnstile"
                        :data-sitekey="turnstileSiteKey"
                    ></div>
                </div>

                <input
                    type="hidden"
                    :name="
                        RequestBodyKeyEnum.Auth_User_Register_BrowserTimeZone
                    "
                    :value="browserTimeZone"
                />
            </ApiForm>
        </template>
    </AuthLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import AuthLayout from '@/Shared/Layout/App/Auth/AuthLayout.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import Button from '@/Shared/Component/Button.vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import GoogleSocialAuthButton from '@/App/User/Component/GoogleSocialAuthButton.vue'
    import MicrosoftSocialAuthButton from '@/App/User/Component/MicrosoftSocialAuthButton.vue'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import useDate from '@/Shared/Hook/useDate'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface ShowRegisterProps {
        appSharedDataData: any
        googleSocialLoginEnabled: boolean
        microsoftSocialLoginEnabled: boolean
        turnstileEnabled: boolean
        turnstileSiteKey: string
    }

    const props = defineProps<ShowRegisterProps>()
    const urlFactory = new UrlFactory()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const apiFormRef = ref<ApiFormRefType>(null)
    const browserTimeZone = useDate().getBrowserTimeZone()
    const hasSocialLoginOptions =
        props.googleSocialLoginEnabled || props.microsoftSocialLoginEnabled
</script>
